// plugins/firebase.ts
import { defineNuxtPlugin } from '#app';
// Import and initialize Firebase
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { getAuth } from 'firebase/auth';
import { getToken, initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check';

export default defineNuxtPlugin(async () => {

  const runTimeConfig = useRuntimeConfig();

  if (!process.client)
    return {};

  const firebaseConfig = {
    apiKey: runTimeConfig.public.firebaseApiKey,
    authDomain: runTimeConfig.public.firebaseAuthDomain,
    projectId: runTimeConfig.public.firebaseProjectId,
    storageBucket: runTimeConfig.public.firebaseStorageBucket,
    messagingSenderId: runTimeConfig.public.firebaseMessagingSenderId,
    appId: runTimeConfig.public.firebaseAppId,
    measurementId: runTimeConfig.public.firebaseMeasurementId
  };

  // Initialize Firebase once on client
  const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

  // Initialize services
  let analytics: Analytics | undefined;
  if (await isSupported()) {
    analytics = getAnalytics(firebaseApp);
  }
  const performance = getPerformance(firebaseApp);
  const auth = getAuth(firebaseApp);

  let appCheck: AppCheck | undefined;
  if (process.client) {
    const recaptchaV3Key = runTimeConfig.public.recaptchaV3Key as string | undefined;
    const appCheckDebugToken = String(runTimeConfig.public.appCheckDebugToken || "").trim();
    const hostname = window.location.hostname.toLowerCase();
    const isLocalhost =
      hostname === "localhost"

    if (recaptchaV3Key) {
      if (isLocalhost) {
        // Local only: use App Check debug mode to avoid reCAPTCHA domain errors.
        ;(self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken || true;
      }

      appCheck = initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaV3Provider(recaptchaV3Key),
        isTokenAutoRefreshEnabled: true
      });

      getToken(appCheck, true).catch((error) => {
        if (isLocalhost) {
          console.warn("[firebase] App Check token init failed in local mode.", error);
        }
        return undefined;
      });
    }
  }

  return {
    provide: {
      firebase: () => firebaseApp,
      analytics: () => analytics,
      auth: () => auth,
      performance: () => performance,
      appCheck: () => appCheck,
    },
  };
});
